import { isMusic24Locale, Music24Locale } from 'fym-shared/src/music24/music24Locales';

/**
 * The picked language, kept in the browser so the first paint after a reload uses it before
 * `GET /users/:id` answers. The server value (`UserModel.music24Locale`) wins when it arrives. No
 * value is "Automatic" (the browser language).
 *
 * Two places write it:
 * - the account: Settings → Account → Language, the language picker of a signed-in page, and the
 *   server value that `UserLocaleSync` applies (source `account`);
 * - a visitor: the language switcher of music24.com and the picker of the sign-in page (no source,
 *   or source `visitor`). The sign-in page then opens in that language, and the first sign-in
 *   saves it to the new account (QA 2026-10-28, lit-1).
 * Sign-out removes only an account value (`UserLocaleSync`).
 */
export const APP_LOCALE_STORAGE_KEY = 'm24Locale';

/** Who wrote `m24Locale`. music24.com writes no source: a value with no source is a visitor pick. */
export const APP_LOCALE_SOURCE_STORAGE_KEY = 'm24LocaleSource';

export type AppLocaleSource = 'account' | 'visitor';

export function readStoredAppLocale(): Music24Locale | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const stored = localStorage.getItem(APP_LOCALE_STORAGE_KEY);
    return isMusic24Locale(stored) ? stored : null;
  } catch (error) {
    console.warn('Could not read the Music24 language from storage', error);
    return null;
  }
}

/** The writer of the stored language: `account`, else `visitor` (also for a value with no source). */
export function readStoredAppLocaleSource(): AppLocaleSource {
  try {
    if (typeof localStorage === 'undefined') {
      return 'visitor';
    }
    return localStorage.getItem(APP_LOCALE_SOURCE_STORAGE_KEY) === 'account' ? 'account' : 'visitor';
  } catch (error) {
    console.warn('Could not read the Music24 language source from storage', error);
    return 'visitor';
  }
}

/** Stores the picked language and its writer. Null removes both ("Automatic"). */
export function storeAppLocale(locale: Music24Locale | null, source: AppLocaleSource = 'account'): void {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    if (locale === null) {
      localStorage.removeItem(APP_LOCALE_STORAGE_KEY);
      localStorage.removeItem(APP_LOCALE_SOURCE_STORAGE_KEY);
    } else {
      localStorage.setItem(APP_LOCALE_STORAGE_KEY, locale);
      localStorage.setItem(APP_LOCALE_SOURCE_STORAGE_KEY, source);
    }
  } catch (error) {
    console.warn('Could not store the Music24 language', error);
  }
}
